import React from 'react'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { TrendingUp, TrendingDown } from 'lucide-react'

function ChangeLabel({ value }) {
  const n = Number(value)
  const positive = n >= 0
  return (
    <span
      className={`flex items-center gap-0.5 font-semibold text-sm tabular-nums ${
        positive ? 'text-emerald-400' : 'text-rose-400'
      }`}
      style={{ fontFamily: 'JetBrains Mono, monospace' }}
    >
      {positive ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
      {positive ? '+' : ''}{n.toFixed(2)}%
    </span>
  )
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  return (
    <div
      className="rounded-lg px-3 py-2 text-xs border"
      style={{ background: '#0d1423', borderColor: '#1f2937', boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }}
    >
      <p
        className="text-slate-500 mb-1.5"
        style={{ fontFamily: 'JetBrains Mono, monospace' }}
      >
        {label}
      </p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color, fontFamily: 'JetBrains Mono, monospace' }}>
          {p.name}: {Number(p.value)?.toFixed(2)}
        </p>
      ))}
    </div>
  )
}

export default function MarketOverview({ chartData = [], stats = {}, loading }) {
  const noData = !loading && chartData.filter(d => d.SPY || d.QQQ).length === 0

  return (
    <div className="rounded-xl border border-gray-800 p-5" style={{ background: '#111827' }}>

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="section-header" style={{ marginBottom: 0 }}>Market Overview</h3>
        {stats.market_sentiment && (
          <span
            className={`text-xs font-bold px-2.5 py-0.5 rounded tracking-wider ${
              stats.market_sentiment === 'BULLISH'
                ? 'text-emerald-400'
                : 'text-rose-400'
            }`}
            style={{
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: '0.65rem',
              background: stats.market_sentiment === 'BULLISH'
                ? 'rgba(16,185,129,0.1)'
                : 'rgba(244,63,94,0.1)',
              border: stats.market_sentiment === 'BULLISH'
                ? '1px solid rgba(16,185,129,0.25)'
                : '1px solid rgba(244,63,94,0.25)',
            }}
          >
            {stats.market_sentiment}
          </span>
        )}
      </div>

      {/* Quick stats row */}
      <div
        className="grid grid-cols-3 gap-4 mb-4 pb-4"
        style={{ borderBottom: '1px solid #1f2937' }}
      >
        <div>
          <p className="text-slate-600 mb-1" style={{ fontSize: '0.65rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            S&amp;P 500
          </p>
          <ChangeLabel value={stats.sp500_change ?? 0} />
          <p className="text-slate-700 mt-0.5" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.62rem' }}>SPY</p>
        </div>
        <div>
          <p className="text-slate-600 mb-1" style={{ fontSize: '0.65rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Nasdaq
          </p>
          <ChangeLabel value={stats.nasdaq_change ?? 0} />
          <p className="text-slate-700 mt-0.5" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.62rem' }}>QQQ</p>
        </div>
        <div>
          <p className="text-slate-600 mb-1" style={{ fontSize: '0.65rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Volatility
          </p>
          {/* No VIX index is collected — VXX is the live proxy, using the same
              VXX_HIGH_THRESHOLD(28)/VXX_HALT_THRESHOLD(35) the live BUY gate trades on. */}
          <span
            className={`font-semibold text-sm tabular-nums ${
              Number(stats.vxx_price) >= 35 ? 'text-rose-400' :
              Number(stats.vxx_price) >= 28 ? 'text-amber-400' : 'text-slate-300'
            }`}
            style={{ fontFamily: 'JetBrains Mono, monospace' }}
          >
            {stats.vxx_price != null ? `$${stats.vxx_price.toFixed(2)}` : '—'}
          </span>
          <p className="text-slate-700 mt-0.5" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.62rem' }}>VXX</p>
        </div>
      </div>

      {/* Chart */}
      {noData ? (
        <div
          className="h-44 flex items-center justify-center rounded-lg"
          style={{ background: '#0f172a', border: '1px dashed #1e293b' }}
        >
          <p
            className="text-slate-700"
            style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem', letterSpacing: '0.1em' }}
          >
            NO INTRADAY DATA — MARKET CLOSED
          </p>
        </div>
      ) : (
        <div className="h-44 -mx-1">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="spyGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="qqqGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
                <filter id="spyGlow">
                  <feGaussianBlur stdDeviation="2" result="blur" />
                  <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
                </filter>
              </defs>
              <CartesianGrid strokeDasharray="2 5" stroke="#1e293b" />
              <XAxis
                dataKey="time"
                tick={{ fontSize: 9, fill: '#475569', fontFamily: 'JetBrains Mono, monospace' }}
                stroke="#1e293b"
              />
              <YAxis
                tick={{ fontSize: 9, fill: '#475569', fontFamily: 'JetBrains Mono, monospace' }}
                stroke="#1e293b"
                domain={['dataMin - 1', 'dataMax + 1']}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="SPY"
                stroke="#3b82f6"
                fill="url(#spyGrad)"
                strokeWidth={1.5}
                name="SPY"
                dot={false}
                strokeOpacity={0.9}
              />
              <Area
                type="monotone"
                dataKey="QQQ"
                stroke="#10b981"
                fill="url(#qqqGrad)"
                strokeWidth={1.5}
                name="QQQ"
                dot={false}
                strokeOpacity={0.9}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}
